import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { SectionTitle, Card } from "../ui";

const FIELD = "w-full rounded-lg border border-sand-300 bg-white px-3 py-2 text-sm text-charcoal-800";
const EMPTY = { category: "visas", label: "", note: "" };

export default function ChecklistManager() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/moving-checklist").then((r) => setData(r.data)).catch(() => setData({ categories: [], items: [] }));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/admin/moving-checklist/${form.id}`, form);
      else await api.post("/admin/moving-checklist", form);
      toast.success("Checklist item saved.");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.label}"?`)) return;
    try { await api.delete(`/admin/moving-checklist/${item.id}`); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const move = async (item, dir) => {
    const ids = data.items.map((i) => i.id);
    const idx = ids.indexOf(item.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try { await api.post("/admin/moving-checklist/reorder", { ids }); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  if (!data) return <p className="text-charcoal-500">Loading…</p>;

  return (
    <div data-testid="admin-checklist">
      <SectionTitle title="Moving checklist" subtitle="The interactive checklist users track their move with — edit items per category." />
      <div className="mb-4 flex items-center justify-between">
        <button data-testid="checklist-new-btn" onClick={() => setEditing({ ...EMPTY })}
          className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-4 py-2 text-sm text-white font-medium hover:bg-terracotta-hover">
          <Plus size={14} /> New item
        </button>
        <a href="/moving-checklist" target="_blank" rel="noreferrer" className="text-sm text-terracotta hover:underline">View public page</a>
      </div>

      {editing && (
        <Card className="mb-5">
          <h3 className="font-medium text-charcoal-800 mb-3">{editing.id ? "Edit item" : "New item"}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <select data-testid="checklist-form-category" className={FIELD} value={editing.category}
              onChange={(e) => setEditing((f) => ({ ...f, category: e.target.value }))}>
              {data.categories.map((c) => <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>)}
            </select>
            <input data-testid="checklist-form-label" className={FIELD} placeholder="Item label *" value={editing.label}
              onChange={(e) => setEditing((f) => ({ ...f, label: e.target.value }))} />
            <div className="sm:col-span-2">
              <input data-testid="checklist-form-note" className={FIELD} placeholder="Helper note (optional)" value={editing.note || ""}
                onChange={(e) => setEditing((f) => ({ ...f, note: e.target.value }))} />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button data-testid="checklist-form-save" onClick={() => editing.label.trim() ? save(editing) : toast.error("Label is required.")}
              className="rounded-full bg-terracotta px-5 py-2 text-sm text-white font-medium hover:bg-terracotta-hover">Save</button>
            <button onClick={() => setEditing(null)} className="rounded-full border border-sand-300 px-5 py-2 text-sm text-charcoal-700 hover:bg-sand-100">Cancel</button>
          </div>
        </Card>
      )}

      {data.categories.map((cat) => {
        const items = data.items.filter((i) => i.category === cat.id);
        return (
          <div key={cat.id} className="mb-6" data-testid={`admin-checklist-cat-${cat.id}`}>
            <h3 className="mb-2 font-serif text-lg text-charcoal-800">{cat.emoji} {cat.label} <span className="text-sm text-charcoal-400">({items.length})</span></h3>
            <div className="space-y-1.5">
              {items.map((item) => (
                <Card key={item.id}>
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-charcoal-800">{item.label}</p>
                      {item.note && <p className="text-xs text-charcoal-500">{item.note}</p>}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button data-testid={`checklist-up-${item.id}`} onClick={() => move(item, -1)} className="p-1.5 text-charcoal-400 hover:text-terracotta"><ArrowUp size={14} /></button>
                      <button data-testid={`checklist-down-${item.id}`} onClick={() => move(item, 1)} className="p-1.5 text-charcoal-400 hover:text-terracotta"><ArrowDown size={14} /></button>
                      <button data-testid={`checklist-edit-${item.id}`} onClick={() => setEditing({ ...item })} className="p-1.5 text-charcoal-400 hover:text-terracotta"><Pencil size={14} /></button>
                      <button data-testid={`checklist-delete-${item.id}`} onClick={() => remove(item)} className="p-1.5 text-charcoal-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </Card>
              ))}
              {items.length === 0 && <Card><p className="text-sm text-charcoal-500">No items in this category yet.</p></Card>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
